import type { BloodGlucoseClassification } from "@/model/BloodGlucoseClassification"
import type { BloodGlucoseFormContext } from "@/model/BloodGlucoseFormContext"
import type {
  BloodGlucoseObservation,
  BloodGlucoseObservationWithReading,
} from "@/model/BloodGlucoseObservation"
import { LocalDate, ZonedDateTime } from "@js-joda/core"

export type DashboardTab = "overview" | "meal-logs" | "measurement"

export const DASHBOARD_TABS: DashboardTab[] = ["overview", "meal-logs", "measurement"]

export type LatestReadingInfo = {
  reading?: number
  classification?: BloodGlucoseClassification
  readingId?: string
}

export type LatestReading = {
  date: string
} & Partial<Record<BloodGlucoseFormContext, LatestReadingInfo>>

const OVERVIEW_CONTEXTS: BloodGlucoseFormContext[] = ["fasting", "breakfast", "lunch", "dinner"]

const FOOD_LOG_CONTEXTS = new Set<BloodGlucoseFormContext>([
  "breakfast",
  "lunch",
  "dinner",
  "snack-before-breakfast",
  "snack-between-breakfast-and-lunch",
  "snack-between-lunch-and-dinner",
  "snack-after-dinner",
])

export const isDashboardTab = (value: string | null): value is DashboardTab =>
  value !== null && DASHBOARD_TABS.includes(value as DashboardTab)

export const hasReading = (
  observation: BloodGlucoseObservation,
): observation is BloodGlucoseObservationWithReading => observation.reading !== null

export const isCriticalClassification = (classification: BloodGlucoseClassification) =>
  classification === "emergency-low" ||
  classification === "critical-low" ||
  classification === "critical-high"

export const needsFeedback = (observation: BloodGlucoseObservationWithReading): boolean =>
  observation.isFormEditable &&
  observation.form === null &&
  !isCriticalClassification(observation.reading.classification)

export const textForClassification = (
  classification: BloodGlucoseClassification,
): string | null => {
  switch (classification) {
    case "pending":
    case "unclassified":
    case "acceptable":
      return null
    case "emergency-low":
    case "critical-low":
      return "Critical low"
    case "low":
      return "Low"
    case "elevated":
      return "Elevated"
    case "critical-high":
      return "High"
    case "impossible":
      return "Impossible"
  }
}

// Single source of truth for classification colors, ported from the old
// app's helpers/dashboard.ts. Total records so a new classification value
// fails the build instead of silently rendering as "no data" grey.
const noReadingColors = {
  background: "#E4E4E4",
  border: "#C5C5C5",
  text: "#000000E6",
}

const criticalLowColors = {
  background: "#FEE2E2",
  border: "#FEE2E2",
  text: "#DC2626",
}

const criticalHighColors = {
  background: "#FEF9C3",
  border: "#FFE21A",
  text: "#CA8A04",
}

const classificationColors: Record<
  BloodGlucoseClassification,
  { background: string; border: string; text: string }
> = {
  "emergency-low": criticalLowColors,
  "critical-low": criticalLowColors,
  low: noReadingColors,
  acceptable: noReadingColors,
  elevated: noReadingColors,
  "critical-high": criticalHighColors,
  impossible: noReadingColors,
  pending: noReadingColors,
  unclassified: noReadingColors,
}

const colorsFor = (classification?: BloodGlucoseClassification) =>
  classification ? classificationColors[classification] : noReadingColors

export const readingValueBackgroundColorFor = (classification?: BloodGlucoseClassification) =>
  colorsFor(classification).background

export const readingValueBorderColorFor = (classification?: BloodGlucoseClassification) =>
  colorsFor(classification).border

export const readingValueTextColorFor = (classification?: BloodGlucoseClassification) =>
  colorsFor(classification).text

export const observationsByDate = <T extends { dateTime: ZonedDateTime }>(
  observations: T[],
): Record<string, T[]> =>
  observations.reduce<Record<string, T[]>>((acc, observation) => {
    const date = observation.dateTime.toLocalDate().toString()
    acc[date] = [...(acc[date] ?? []), observation].sort((a, b) => a.dateTime.compareTo(b.dateTime))
    return acc
  }, {})

const latestReadingIn = (
  observations: BloodGlucoseObservationWithReading[],
): LatestReadingInfo | undefined => {
  const latest = [...observations].sort((a, b) =>
    b.reading.dateTime.compareTo(a.reading.dateTime),
  )[0]

  if (!latest) return undefined

  return {
    reading: latest.reading.bloodGlucoseInMilligramsPerDeciliter,
    classification: latest.reading.classification,
    readingId: latest.reading.id,
  }
}

const parsedTimelineFor = (
  observationsByDate: Record<string, BloodGlucoseObservationWithReading[]>,
): LatestReading[] =>
  Object.entries(observationsByDate).map(([date, observations]) => {
    const result: LatestReading = { date }
    for (const context of OVERVIEW_CONTEXTS) {
      const byContext = observations.filter((observation) => observation.form?.context === context)
      const latest = latestReadingIn(byContext)
      if (latest) result[context] = latest
    }
    return result
  })

export const overviewReadings = (
  observations: BloodGlucoseObservation[],
  weekStart: ZonedDateTime,
): LatestReading[] => {
  const observationsWithReading = observations.filter(hasReading)
  const filtered = observationsWithReading.filter(
    (observation) => !observation.form || OVERVIEW_CONTEXTS.includes(observation.form.context),
  )
  const parsed = parsedTimelineFor(observationsByDate(filtered))
  const byDate = new Map(parsed.map((row) => [row.date, row]))

  return Array.from({ length: 7 }, (_, index) => {
    const date = weekStart.plusDays(index).toLocalDate().toString()
    return byDate.get(date) ?? { date }
  })
}

export const measurementsByDate = (
  observations: BloodGlucoseObservation[],
): Record<string, BloodGlucoseObservationWithReading[]> =>
  observationsByDate(observations.filter(hasReading))

export const foodLogsByDate = (
  observations: BloodGlucoseObservation[],
): Record<string, BloodGlucoseObservation[]> =>
  observationsByDate(
    observations.filter((observation) => {
      const form = observation.form
      if (!form || !FOOD_LOG_CONTEXTS.has(form.context)) return false
      return form.mealContents !== null || form.carbsInGrams !== null
    }),
  )

export const isPastDashboardDate = (date: string): boolean =>
  LocalDate.parse(date).isBefore(LocalDate.now()) || LocalDate.parse(date).isEqual(LocalDate.now())
